import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  InstalledPlugin,
  PluginCatalogItem,
  PluginContributionKind,
} from '@crowdex/plugin-contracts';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { Input } from '@crowdex/ui/components/input';
import { SegmentedControl, SegmentedControlItem } from '@crowdex/ui/components/segmented-control';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronLeft, ChevronRight, LoaderCircle, Package, Search } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  ConnectAccountMenu,
  ConnectionsView,
  useConnections,
} from '@/components/plugins-page/connections-view';
import { CustomPlugins } from '@/components/plugins-page/custom-plugins';
import {
  FilterPill,
  InstalledPlugins,
  KIND_FILTERS,
  PluginsLoading,
} from '@/components/plugins-page/installed-plugins';
import { CONTRIBUTION_LABELS, PluginCard } from '@/components/plugins-page/plugin-card';
import { PluginConfiguration } from '@/components/plugins-page/plugin-configuration';
import { PluginDetail } from '@/components/plugins-page/plugin-detail';
import { PluginMarketplacesDialog } from '@/components/plugins-page/plugin-marketplaces-dialog';
import { usePluginPageFocus } from '@/components/plugins-page/use-plugin-page-focus';
import {
  SettingSelect,
  SettingsPageHeader,
  settingButtonClass,
} from '@/components/settings-page/setting-controls';
import { isLocalMachine } from '@/lib/local-runtime';
import { useRouter } from '@/lib/navigation';
import {
  PLUGIN_SECTIONS,
  type PluginSection,
  pluginPagePath,
  pluginsPath,
} from '@/lib/plugins/plugin-sections';
import { catalogPageCount } from '@/lib/plugins-page/catalog';
import { isPluginConnected, pluginAccountServices } from '@/lib/plugins-page/plugin-status';
import type { CatalogPage } from '@/lib/plugins-page/types';
import { usePluginManagement } from '@/lib/plugins-page/use-plugin-management';
import { navigateApp } from '@/vite/navigation-store';

const ALL_CATEGORIES = '__all';

/** What Settings › Connections says under its title. */
const CONNECTIONS_DESCRIPTION = 'The accounts of your services that plugins and agents act with.';

/** Browse / Installed / Custom, at the top of Settings › Plugins (D29, phone 26). */
function Segments({ section, phone }: { section: PluginSection; phone: boolean }) {
  const router = useRouter();
  return (
    <SegmentedControl
      aria-label="Plugins views"
      className={phone ? 'flex w-full' : undefined}
      // The views replace each other: back leaves Plugins, as it leaves any section.
      onValueChange={(value) => router.replace(pluginsPath(value as PluginSection))}
      value={section}
    >
      {PLUGIN_SECTIONS.map((item) => (
        <SegmentedControlItem key={item.id} value={item.id}>
          {item.label}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

/**
 * Settings › Connections (D32, phone 29): the accounts of the user's services, one per row with
 * its state; "Connect an account" adds one.
 */
export function ConnectionsSettingsSection({
  connection = null,
  phone,
}: {
  /** A connection to sign in to again (`&connection=`: an app's or a notification's Reconnect). */
  connection?: string | null;
  phone: boolean;
}) {
  const connections = useConnections();
  return (
    <>
      <SettingsPageHeader
        aside={<ConnectAccountMenu data={connections} />}
        description={CONNECTIONS_DESCRIPTION}
        title="Connections"
      />
      <ConnectionsView data={connections} focus={connection} phone={phone} />
    </>
  );
}

/**
 * Settings › Plugins (D29–D33b, phone 26–30): the catalog, what is installed and the user's own
 * plugins, in a segmented control at the top; a plugin's page is a sub-page with one main button.
 */
export function PluginsSettingsSection({
  section,
  pluginName = null,
  phone,
}: {
  section: PluginSection;
  /** The plugin whose page is open, if any. */
  pluginName?: string | null;
  phone: boolean;
}) {
  const router = useRouter();
  const [opened, setOpened] = useState<PluginCatalogItem | null>(null);
  const [marketplacesOpen, setMarketplacesOpen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  usePluginPageFocus(pluginName, pageRef);
  const connections = useConnections();
  const management = usePluginManagement(section, pluginName, (name) =>
    router.push(pluginPagePath(section, name)),
  );
  const {
    activeCategory,
    activeKind,
    busyPlugin,
    catalog,

    error,
    install,
    installed,
    listed,
    installedByName,
    loading,
    page,
    query,
    reloadCatalog,
    searchCatalog,
    setActiveCategory,
    setActiveKind,
    setContributionEnabled,
    setPage,
    setQuery,
    shownPlugins,
    toggle,
    uninstall,
  } = management;
  // The hub's connections, by type: whether a plugin is connected.
  const accountTypes = (connections.listing?.connections ?? []).map(
    (connection) => connection.type,
  );
  const currentSection = PLUGIN_SECTIONS.find((item) => item.id === section) ?? PLUGIN_SECTIONS[0];

  const openPlugin = (plugin: PluginCatalogItem | InstalledPlugin) => {
    setOpened(plugin);
    router.push(pluginPagePath(section, plugin.name));
  };
  const detailLocal = pluginName === null ? undefined : installedByName.get(pluginName);
  const detailPlugin =
    pluginName === null
      ? null
      : (shownPlugins.find((plugin) => plugin.name === pluginName) ??
        detailLocal ??
        (opened?.name === pluginName ? opened : null));

  // A plugin's page opened by its link, not from the list: look it up in the catalog. A service's
  // generated entry (`service.<id>`) may be replaced by a plugin about the service, under its own name.
  const missing = pluginName !== null && detailPlugin === null && !loading;
  const aboutService = pluginName?.startsWith('service.')
    ? pluginName.slice('service.'.length)
    : null;
  const installedAboutService =
    aboutService === null ? undefined : installed.find((plugin) => plugin.service === aboutService);
  useEffect(() => {
    if (!missing || pluginName === null) return;
    if (installedAboutService !== undefined) {
      navigateApp(pluginPagePath(section, installedAboutService.name), true);
      return;
    }
    let cancelled = false;
    const parameters = new URLSearchParams({
      query: aboutService ?? pluginName,
      pageSize: '48',
      host: isLocalMachine() ? 'desktop' : 'cloud',
    });
    void apiFetch<CatalogPage>(`/api/plugins/catalog?${parameters}`)
      .then((found) => {
        const match =
          found.items.find((item) => item.name === pluginName) ??
          (aboutService === null
            ? undefined
            : found.items.find((item) => item.service === aboutService));
        if (cancelled || !match) return;
        setOpened(match);
        if (match.name !== pluginName) navigateApp(pluginPagePath(section, match.name), true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [aboutService, installedAboutService, missing, pluginName, section]);

  const accountCount = (plugin: InstalledPlugin) => {
    const services = pluginAccountServices(plugin);
    return accountTypes.filter((type) => services.has(type)).length;
  };
  const pendingUpdates = installed.filter((plugin) => plugin.crowdex?.pending !== undefined);
  const description =
    section === 'installed'
      ? `${listed.length} installed${pendingUpdates.length > 0 ? ` · ${pendingUpdates.length} ${pendingUpdates.length === 1 ? 'update' : 'updates'} available` : ''}`
      : currentSection.description;

  const header = phone ? (
    // A phone names the screen in its header: the views take the whole width.
    <div className="mb-4">
      <Segments phone section={section} />
    </div>
  ) : (
    <SettingsPageHeader
      aside={
        <div className="flex items-center gap-1">
          <Segments phone={false} section={section} />
          <button
            className="flex h-control-sm items-center gap-1.5 rounded-control px-3 text-foreground text-sm hover:bg-foreground/5"
            onClick={() => setMarketplacesOpen(true)}
            type="button"
          >
            <Package aria-hidden className="size-3.5" /> Marketplaces
          </button>
        </div>
      }
      description={description}
      title="Plugins"
    />
  );

  let body: ReactNode = null;
  if (section === 'custom')
    body = (
      <CustomPlugins
        accountCount={accountCount}
        management={management}
        onOpen={openPlugin}
        onOpenMarketplaces={() => setMarketplacesOpen(true)}
        phone={phone}
      />
    );
  else if (section === 'installed')
    body = (
      <InstalledPlugins
        accountCount={accountCount}
        management={management}
        onOpen={openPlugin}
        phone={phone}
      />
    );
  else {
    const categories = catalog?.categories ?? [];
    const unfiltered =
      page === 1 && query.trim() === '' && activeKind === null && activeCategory === null;
    const featured =
      unfiltered && !phone
        ? (shownPlugins.find(
            (plugin) => !installedByName.has(plugin.name) && plugin.availability !== 'coming-soon',
          ) ?? null)
        : null;
    const cards = shownPlugins.filter((plugin) => plugin !== featured);
    body = (
      <>
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <form
            className={cn('relative', phone ? 'w-full' : 'w-90 max-w-full')}
            onSubmit={searchCatalog}
          >
            <Search
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              aria-label="Search plugins"
              className="h-9 w-full rounded-xl bg-control pr-3 pl-9 text-sm max-md:h-11 max-md:text-base"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search connectors, skills, agents…"
              type="search"
              value={query}
            />
          </form>
          <div className={cn('flex items-center gap-2', phone && '-mx-4 overflow-x-auto px-4')}>
            {!phone && (
              <FilterPill
                onClick={() => {
                  setActiveKind(null);
                  setPage(1);
                }}
                selected={activeKind === null}
              >
                All
              </FilterPill>
            )}
            {KIND_FILTERS.map((kind) => (
              <FilterPill
                key={kind}
                onClick={() => {
                  setActiveKind(
                    activeKind === kind && phone ? null : (kind as PluginContributionKind),
                  );
                  setPage(1);
                }}
                selected={activeKind === kind}
              >
                {CONTRIBUTION_LABELS[kind]}
              </FilterPill>
            ))}
          </div>
          {categories.length > 0 && !phone && (
            <SettingSelect
              className="ml-auto"
              label="Filter plugins by category"
              onValueChange={(value) => {
                setActiveCategory(value === ALL_CATEGORIES ? null : value);
                setPage(1);
              }}
              options={[
                { value: ALL_CATEGORIES, label: 'All categories' },
                ...[...categories]
                  .sort((left, right) => left.localeCompare(right))
                  .map((category) => ({ value: category, label: category })),
              ]}
              value={activeCategory ?? ALL_CATEGORIES}
            />
          )}
        </div>
        {catalog?.sources
          .filter((source) => source.error !== undefined)
          .map((source) => (
            <p className="mb-3 text-destructive text-xs" key={`${source.id}:${source.url}`}>
              {source.displayName} is unavailable: {source.error}
            </p>
          ))}
        {loading ? (
          <PluginsLoading />
        ) : shownPlugins.length === 0 ? (
          <p className="rounded-2xl border border-hairline border-dashed py-16 text-center text-muted-foreground text-sm">
            No matching plugins.
          </p>
        ) : (
          <>
            {featured && (
              <div className="mb-3">
                <PluginCard
                  busy={busyPlugin === featured.name}
                  connected={false}
                  featured
                  local={undefined}
                  onInstall={install}
                  onOpen={openPlugin}
                  plugin={featured}
                />
              </div>
            )}
            <div className={phone ? '' : 'grid gap-3 md:grid-cols-2 xl:grid-cols-3'}>
              {cards.map((plugin) => {
                const local = installedByName.get(plugin.name);
                return (
                  <PluginCard
                    busy={busyPlugin === plugin.name}
                    connected={local !== undefined && isPluginConnected(local, accountTypes)}
                    key={plugin.name}
                    local={local}
                    onInstall={install}
                    onOpen={openPlugin}
                    phone={phone}
                    plugin={plugin}
                  />
                );
              })}
            </div>
          </>
        )}
        {catalog && catalog.total > catalog.pageSize && (
          <div className="mt-7 flex items-center justify-center gap-3 text-sm">
            <button
              aria-label="Previous page"
              className="flex size-8 items-center justify-center rounded-full bg-control disabled:opacity-40"
              disabled={page === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              type="button"
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-muted-foreground">
              Page {page} of {catalogPageCount(catalog.total, catalog.pageSize)}
            </span>
            <button
              aria-label="Next page"
              className="flex size-8 items-center justify-center rounded-full bg-control disabled:opacity-40"
              disabled={page >= catalogPageCount(catalog.total, catalog.pageSize)}
              onClick={() => setPage((value) => value + 1)}
              type="button"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        )}
        {catalog && catalog.sources.length > 0 && (
          <p className="mt-8 text-muted-foreground text-xs">
            From {catalog.sources.map((source) => source.displayName).join(', ')}
            {catalog.sources.some((source) => source.id === 'mcp-registry') &&
              ' · MCP Registry servers appear when you search'}
            . Review each plugin’s license and permissions before installing.
          </p>
        )}
      </>
    );
  }

  return (
    // Its own box (no layout of its own) for the focus to follow a plugin's page in.
    <div className="contents" ref={pageRef}>
      {pluginName !== null ? (
        detailPlugin === null ? (
          <div className="flex h-56 items-center justify-center gap-2 text-muted-foreground text-sm">
            {loading ? (
              <>
                <LoaderCircle className="size-4 animate-spin" /> Loading plugin…
              </>
            ) : (
              <span>
                This plugin is not in this list any more.{' '}
                <button
                  className="underline"
                  onClick={() => router.push(pluginsPath(section))}
                  type="button"
                >
                  Back to Plugins
                </button>
              </span>
            )}
          </div>
        ) : (
          <PluginDetail
            backLabel="Plugins"
            busy={busyPlugin === detailPlugin.name}
            connected={detailLocal !== undefined && isPluginConnected(detailLocal, accountTypes)}
            local={detailLocal}
            onApproveUpdate={(plugin) => void management.approveUpdate(plugin)}
            onBack={() => router.push(pluginsPath(section))}
            onCommandUnsandboxed={(plugin, command, allowed) =>
              void management.setCommandUnsandboxed(plugin, command, allowed)
            }
            onContributionToggle={(plugin, key, enabled) =>
              void setContributionEnabled(plugin, key, enabled)
            }
            onInstall={install}
            onPublish={(plugin) => void management.publish(plugin)}
            onRefreshLocal={
              management.desktop ? (plugin) => void management.refreshLocal(plugin) : undefined
            }
            onToggle={toggle}
            onUninstall={uninstall}
            phone={phone}
            plugin={detailPlugin}
            settings={
              detailLocal && <PluginConfiguration management={management} plugin={detailLocal} />
            }
          />
        )
      ) : (
        <>
          {header}
          {error && (
            <p className="mb-5 rounded-2xl bg-destructive/10 px-4 py-3 text-destructive text-sm">
              {error}
            </p>
          )}
          {/* A list that could not be read says so, with Retry: never "No matching plugins". */}
          {management.loadError !== null && !loading ? (
            <InlineMessage
              actions={
                <button
                  className={settingButtonClass()}
                  onClick={management.retryLoad}
                  type="button"
                >
                  Retry
                </button>
              }
              title="Couldn't load plugins."
              tone="error"
            >
              {management.loadError}
            </InlineMessage>
          ) : (
            body
          )}
        </>
      )}
      <PluginMarketplacesDialog
        onChange={() => void reloadCatalog()}
        onClose={() => setMarketplacesOpen(false)}
        open={marketplacesOpen}
      />
    </div>
  );
}
